import { useProfileContext } from "@hooks/context/useProfileContext";
import { useOrganisationFrameworks } from "@hooks/crud/projectFramework/useOrganisationFrameworks";
import { createContext, FC, ReactNode, useContext, useMemo } from "react";
import { OrganisationFrameworkSummaryLineItem } from "catchment-common/interfaces/projectFramework";

type OrganisationFrameworksContextType = {
  frameworks: OrganisationFrameworkSummaryLineItem[];
  hasLoaded: boolean;
  isLoading: boolean;
  refresh: () => void;
};

export const OrganisationFrameworksContext =
  createContext<OrganisationFrameworksContextType>({
    frameworks: [],
    hasLoaded: false,
    isLoading: false,
    refresh: () => {}
  });

type OrganisationFrameworksProviderProps = {
  children: ReactNode;
};

/**
 * The frameworks the organisation's projects report against, fetched ONCE for
 * the whole organisation layout.
 *
 * A context rather than a hook each consumer calls, because there are now two
 * of them mounted at the same time — the sidebar's Compliance rows, and the org
 * Compliance page they lead to — and `useOrganisationFrameworks` fetches on
 * mount, so the second consumer was a second identical request on every visit.
 * The list is the same for both by definition: it is scoped to the
 * organisation, not to anything either consumer knows.
 *
 * Gated on `isContractor`, exactly as the sidebar's own fetch was: Compliance
 * is the contractor's view — the frameworks ITS projects answer — and the same
 * flag gates project set-up. A non-contractor organisation therefore sees no
 * Compliance rows AND, if it reaches the page by URL, no frameworks on it,
 * which is the one consistent answer rather than two.
 */
export const OrganisationFrameworksProvider: FC<
  OrganisationFrameworksProviderProps
> = ({ children }) => {
  const { userOrganisation, userProfile } = useProfileContext();
  const isContractor = userOrganisation?.isContractor ?? false;

  const { frameworks, hasLoaded, isLoading, refresh } =
    useOrganisationFrameworks(
      isContractor ? userProfile?.organisationId : undefined
    );

  const value = useMemo(
    () => ({ frameworks, hasLoaded, isLoading, refresh }),
    [frameworks, hasLoaded, isLoading, refresh]
  );

  return (
    <OrganisationFrameworksContext.Provider value={value}>
      {children}
    </OrganisationFrameworksContext.Provider>
  );
};

export const useOrganisationFrameworksContext = () =>
  useContext(OrganisationFrameworksContext);
